import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AddressString,
  AssetAllocation,
  AvailableValueToken,
  DepositWithdrawalAsset,
  Fees,
  Paginator,
  PoolDescription,
  RiskFactor,
  StatusPool,
  SwapTransaction,
  Transaction,
  VaultType,
} from 'types'
import { FeeTransactions } from 'types/fee'

const START_PAGE = 1
const PAGE_LIMIT = 10

type SwapTransactionsData = Paginator & {
  data: SwapTransaction[]
}

type TransactionsData = Paginator & {
  data: Array<Transaction>
}

type DepositWithdrawalAssetData = Paginator & {
  data: Array<DepositWithdrawalAsset>
}

type FeeData = Paginator & {
  data: Array<FeeTransactions>
}

export type PoolLimits = {
  deposit_approve: string
  deposit_min: string
  withdraw_delay: number
  withdraw_lockup: number
}

export type PoolData = {
  assetsAllocation: AssetAllocation[]
  chainIds: number[]
  createdAt: Date
  customers: number
  dayReturn?: number
  decimals: number
  description: PoolDescription
  fees: Fees
  fundAddress: AddressString | null | undefined
  fundShare: number
  isPriceValid?: boolean
  isRWA: boolean
  limits: PoolLimits
  maxDrawdown: number
  monthReturn?: number
  netTargetApy?: number
  revenue: string
  riskFactor?: RiskFactor
  sevenDayApy?: number
  sixMonthReturn?: number
  sixMonthReturnEst?: number
  strategyLaunched: Date | null
  targetApy: number
  threeMonthReturn?: number
  tokenPrice?: number
  totalDeposited: string
  valueManaged: string
  vaultType?: VaultType
  weekReturn?: number
  yearReturn?: number
  yearReturnEst?: number
  yearToDate?: number
}
export type ClientVerification = {
  signature: string
  tier: number
  validTill: number
}

type State = {
  address: AddressString | null | undefined
  assetAllocationUpdated: Date | null
  assetAllocationUpdatedNoAum: Date | null
  availableValueTokens: AvailableValueToken[]
  clientVerification: ClientVerification
  decimals: number | undefined
  depositAssetsChangesData: DepositWithdrawalAssetData
  depositPendingTimestamps: number[]
  depositWithdrawalAssetData: DepositWithdrawalAssetData
  feeData: FeeData
  id: null | string | undefined
  investorTransactionsData: TransactionsData
  minClientTier: number
  poolData: PoolData
  status: StatusPool | undefined
  swapTransactionsData: SwapTransactionsData
  transactionsData: TransactionsData
  withdrawalAssetsChangesData: DepositWithdrawalAssetData
}

const initialState: State = {
  address: undefined,
  assetAllocationUpdated: null,
  assetAllocationUpdatedNoAum: null,
  availableValueTokens: [],
  clientVerification: {
    signature: '0x0',
    tier: 0,
    validTill: 0,
  },
  decimals: undefined,
  depositAssetsChangesData: {
    count: 0,
    data: [],
    page: START_PAGE,
    pageCount: 0,
    pageLimit: PAGE_LIMIT,
    total: 0,
  },
  depositPendingTimestamps: [],
  depositWithdrawalAssetData: {
    count: 0,
    data: [],
    page: START_PAGE,
    pageCount: 0,
    pageLimit: PAGE_LIMIT,
    total: 0,
  },
  feeData: {
    count: 0,
    data: [],
    page: START_PAGE,
    pageCount: 0,
    pageLimit: PAGE_LIMIT,
    total: 0,
  },
  id: undefined,
  investorTransactionsData: {
    count: 0,
    data: [],
    page: START_PAGE,
    pageCount: 0,
    pageLimit: PAGE_LIMIT,
    total: 0,
  },
  minClientTier: undefined,
  poolData: {
    assetsAllocation: [],
    chainIds: [],
    createdAt: undefined,
    customers: 0,
    dayReturn: undefined,
    decimals: undefined,
    description: undefined,
    fees: undefined,
    fundAddress: undefined,
    fundShare: undefined,
    isPriceValid: undefined,
    isRWA: undefined,
    limits: undefined,
    maxDrawdown: undefined,
    monthReturn: undefined,
    netTargetApy: undefined,
    revenue: '',
    riskFactor: undefined,
    sevenDayApy: undefined,
    sixMonthReturn: undefined,
    sixMonthReturnEst: undefined,
    strategyLaunched: undefined,
    targetApy: undefined,
    threeMonthReturn: undefined,
    tokenPrice: 0,
    totalDeposited: '',
    valueManaged: '',
    vaultType: undefined,
    weekReturn: undefined,
    yearReturn: undefined,
    yearReturnEst: undefined,
    yearToDate: undefined,
  },
  status: undefined,
  swapTransactionsData: {
    count: 0,
    data: [],
    page: START_PAGE,
    pageCount: 0,
    pageLimit: PAGE_LIMIT,
    total: 0,
  },
  transactionsData: {
    count: 0,
    data: [],
    page: START_PAGE,
    pageCount: 0,
    pageLimit: PAGE_LIMIT,
    total: 0,
  },
  withdrawalAssetsChangesData: {
    count: 0,
    data: [],
    page: START_PAGE,
    pageCount: 0,
    pageLimit: PAGE_LIMIT,
    total: 0,
  },
}

export const poolSlice = createSlice({
  initialState,
  name: 'pool',
  reducers: {
    addDepositAssetsChangesData: (state, { payload }: PayloadAction<DepositWithdrawalAsset[]>) => {
      if (state.depositAssetsChangesData.page === 1) {
        state.depositAssetsChangesData.data = [...payload, ...state.depositAssetsChangesData.data].slice(0, 10)
      }
      state.depositAssetsChangesData.total += payload.length
    },
    addDepositPendingTimestamp: (state) => {
      state.depositPendingTimestamps.push(Date.now())
    },
    addDepositWithdrawalAsset: (state, { payload }: PayloadAction<DepositWithdrawalAsset[]>) => {
      if (state.depositWithdrawalAssetData.page === 1) {
        state.depositWithdrawalAssetData.data = [...payload, ...state.depositWithdrawalAssetData.data].slice(0, 10)
      }
      state.depositWithdrawalAssetData.total += payload.length
    },
    addFee: (state, { payload }: PayloadAction<FeeTransactions[]>) => {
      if (state.feeData.page === 1) {
        state.feeData.data = [...payload, ...state.feeData.data].slice(0, 3)
      }
      state.feeData.total += payload.length
    },
    addInvestorTransactions: (state, { payload }: PayloadAction<Transaction[]>) => {
      if (state.investorTransactionsData.page === 1) {
        state.investorTransactionsData.data = [...payload, ...state.investorTransactionsData.data].slice(0, 10)
      }
      state.investorTransactionsData.total += payload.length
    },
    addSwapTransactions: (state, { payload }: PayloadAction<SwapTransaction[]>) => {
      // Like shit
      if (state.swapTransactionsData.page === 1) {
        const existIds = new Set(state.swapTransactionsData.data.map((i) => i.id))
        const newData = payload.filter((i) => !existIds.has(i.id))
        state.swapTransactionsData.data = [...newData, ...state.swapTransactionsData.data].slice(0, 10)
      }
      state.swapTransactionsData.total += payload.length
    },
    addTransactions: (state, { payload }: PayloadAction<Transaction[]>) => {
      if (state.transactionsData.page === 1) {
        const existIds = new Set(state.transactionsData.data.map((i) => i.id))
        const newData = payload.filter((i) => !existIds.has(i.id))
        state.transactionsData.data = [...newData, ...state.transactionsData.data].slice(0, 10)
      }
      state.transactionsData.total += payload.length
    },
    addWithdrawalAssetsChangesData: (state, { payload }: PayloadAction<DepositWithdrawalAsset[]>) => {
      if (state.withdrawalAssetsChangesData.page === 1) {
        state.withdrawalAssetsChangesData.data = [...payload, ...state.withdrawalAssetsChangesData.data].slice(0, 10)
      }
      state.withdrawalAssetsChangesData.total += payload.length
    },
    clearAllDepositPendingTimestamps: (state) => {
      state.depositPendingTimestamps = []
    },
    resetPoolData: (state) => {
      state.poolData = initialState.poolData
    },
    setAssetAllocationUpdated: (state) => {
      state.assetAllocationUpdated = new Date()
    },
    setAssetAllocationUpdatedNoAum: (state) => {
      state.assetAllocationUpdatedNoAum = new Date()
    },
    setClientVerification: (state, { payload }: PayloadAction<ClientVerification>) => {
      state.clientVerification = payload
    },
    setDepositAssetsChangesData: (state, { payload }: PayloadAction<DepositWithdrawalAssetData>) => {
      state.depositAssetsChangesData = { ...state.depositAssetsChangesData, ...payload }
    },
    setDepositAssetsChangesDataPage: (state, { payload }: PayloadAction<number>) => {
      state.depositAssetsChangesData.page = payload
    },
    setDepositWithdrawalAssetData: (state, { payload }: PayloadAction<DepositWithdrawalAssetData>) => {
      state.depositWithdrawalAssetData = { ...state.depositWithdrawalAssetData, ...payload }
    },
    setDepositWithdrawalAssetDataPage: (state, { payload }: PayloadAction<number>) => {
      state.depositWithdrawalAssetData.page = payload
    },
    setFeeData: (state, { payload }: PayloadAction<FeeData>) => {
      state.feeData = { ...state.feeData, ...payload }
    },
    setFeeDataPage: (state, { payload }: PayloadAction<number>) => {
      state.feeData.page = payload
    },
    setInvestorTransactionsData: (state, { payload }: PayloadAction<TransactionsData>) => {
      state.investorTransactionsData = { ...state.investorTransactionsData, ...payload }
    },
    setInvestorTransactionsDataPage: (state, { payload }: PayloadAction<number>) => {
      state.investorTransactionsData.page = payload
    },
    setPool: (
      state,
      {
        payload,
      }: PayloadAction<{
        address: string
        availableValueTokens: AvailableValueToken[]
        decimals: number
        id: string
        minClientTier: number
      }>,
    ) => {
      state.id = payload.id
      state.address = payload.address as AddressString
      state.decimals = payload.decimals
      state.minClientTier = payload.minClientTier
      state.availableValueTokens = payload.availableValueTokens
    },
    setPoolData: (state, { payload }: PayloadAction<PoolData>) => {
      state.poolData = payload
    },
    setPoolDataNoAUM: (state, { payload }: PayloadAction<PoolData>) => {
      state.poolData = {
        ...payload,
        valueManaged: state.poolData.valueManaged,
      }
    },
    setPoolStatus: (state, { payload }: PayloadAction<{ id: string; status: StatusPool }[]>) => {
      payload.forEach((item) => {
        if (state.id === item.id) {
          state.status = item.status
        }
      })
    },
    setSwapTransactionsData: (state, { payload }: PayloadAction<SwapTransactionsData>) => {
      state.swapTransactionsData = { ...state.swapTransactionsData, ...payload }
    },
    setSwapTransactionsDataPage: (state, { payload }: PayloadAction<number>) => {
      state.swapTransactionsData.page = payload
    },
    setTransactionsData: (state, { payload }: PayloadAction<TransactionsData>) => {
      state.transactionsData = { ...state.transactionsData, ...payload }
    },
    setTransactionsDataPage: (state, { payload }: PayloadAction<number>) => {
      state.transactionsData.page = payload
    },
    setWithdrawalAssetsChangesData: (state, { payload }: PayloadAction<DepositWithdrawalAssetData>) => {
      state.withdrawalAssetsChangesData = { ...state.withdrawalAssetsChangesData, ...payload }
    },
    setWithdrawalAssetsChangesDataPage: (state, { payload }: PayloadAction<number>) => {
      state.withdrawalAssetsChangesData.page = payload
    },
    shiftDepositPendingTimestamp: (state, { payload }: PayloadAction<unknown[]>) => {
      state.depositPendingTimestamps.splice(0, payload.length)
    },
    updateDepositAssetsChangesData: (state, { payload }: PayloadAction<DepositWithdrawalAsset[]>) => {
      const filtered = state.depositAssetsChangesData.data.filter(
        (deposit) => !payload.find((payloadItem) => payloadItem.id === deposit.id),
      )
      state.depositAssetsChangesData.data = [...filtered]
    },
    updateDepositWithdrawalAssetData: (state, { payload }: PayloadAction<DepositWithdrawalAsset[]>) => {
      const filtered = state.depositWithdrawalAssetData.data.filter(
        (withdrawal) => !payload.find((payloadItem) => payloadItem.id === withdrawal.id),
      )
      state.depositWithdrawalAssetData.data = [...filtered]
    },
    updateWithdrawalAssetsChangesData: (state, { payload }: PayloadAction<DepositWithdrawalAsset[]>) => {
      const filtered = state.withdrawalAssetsChangesData.data.filter(
        (withdrawal) => !payload.find((payloadItem) => payloadItem.id === withdrawal.id),
      )

      state.withdrawalAssetsChangesData.data = [...filtered]
    },
  },
})

export const {
  addDepositAssetsChangesData,
  addDepositPendingTimestamp,
  addDepositWithdrawalAsset,
  addFee,
  addInvestorTransactions,
  addSwapTransactions,
  addTransactions,
  addWithdrawalAssetsChangesData,
  clearAllDepositPendingTimestamps,
  setAssetAllocationUpdated,
  setAssetAllocationUpdatedNoAum,
  setClientVerification,
  setDepositAssetsChangesData,
  setDepositAssetsChangesDataPage,
  setDepositWithdrawalAssetData,
  setDepositWithdrawalAssetDataPage,
  setFeeData,
  setFeeDataPage,
  setInvestorTransactionsData,
  setInvestorTransactionsDataPage,
  setPool,
  setPoolData,
  setPoolDataNoAUM,
  setPoolStatus,
  setSwapTransactionsData,
  setSwapTransactionsDataPage,
  setTransactionsData,
  setTransactionsDataPage,
  setWithdrawalAssetsChangesData,
  setWithdrawalAssetsChangesDataPage,
  shiftDepositPendingTimestamp,
  updateDepositAssetsChangesData,
  updateDepositWithdrawalAssetData,
  updateWithdrawalAssetsChangesData,
} = poolSlice.actions
